import React, { useState, useEffect } from 'react';

interface LoginButtonProps {
    className?: string;
    text?: string;
    provider?: 'github' | 'google' | 'aad' | 'microsoft';
}

const isInAppWebView = (): boolean => {
    if (typeof window === 'undefined' || !window.navigator) return false;
    const ua = window.navigator.userAgent || window.navigator.vendor || '';
    return /FBAN|FBAV|Instagram|LinkedInApp|Line|MicroMessenger|musical_ly|Snapchat|Twitter|ByteDance/i.test(ua);
};

const LoginButton: React.FC<LoginButtonProps> = ({ 
    className = "btn-azure", 
    text,
    provider = 'github' 
}) => {
    // Initial state check from localStorage (scoped to this provider, cleared if returning with auth error)
    const [isLoggingIn, setIsLoggingIn] = useState(() => {
        if (typeof window !== 'undefined' && (window.location.search.includes('auth_error=') || window.location.search.includes('error='))) {
            localStorage.removeItem('synclothe_auth_pending');
            return false;
        }
        return localStorage.getItem('synclothe_auth_pending') === provider;
    });
    const [webViewWarning, setWebViewWarning] = useState<string | null>(null);

    const getTargetUrl = () => {
        const postRedirect = encodeURIComponent(window.location.origin + '/');
        switch (provider) {
            case 'google':
                return `/.auth/login/google?post_login_redirect_uri=${postRedirect}`;
            case 'aad':
            case 'microsoft':
                return `/.auth/login/aad?post_login_redirect_uri=${postRedirect}`;
            case 'github':
            default:
                return `/.auth/login/github?post_login_redirect_uri=${postRedirect}`;
        }
    };

    const getDefaultText = () => {
        switch (provider) {
            case 'google':
                return 'Authorize via Google';
            case 'aad':
            case 'microsoft':
                return 'Authorize via Microsoft';
            case 'github':
            default:
                return 'Authorize via GitHub';
        }
    };

    const handleLogin = () => {
        // Guard against Google disallowed_useragent 403 in social webviews
        if (provider === 'google' && isInAppWebView()) {
            setWebViewWarning("Google blocks login in social in-app browsers. Use GitHub/Microsoft or open in Safari/Chrome.");
            return;
        }

        setIsLoggingIn(true);
        // Persist provider-scoped state so only the clicked button shows redirecting state
        localStorage.setItem('synclothe_auth_pending', provider);
        const targetUrl = getTargetUrl();
        if (window.top) {
            window.top.location.href = targetUrl;
        } else {
            window.location.href = targetUrl;
        }
    };

    // Clean up the flag if the user hits browser Back (pageshow/bfcache) or stays on the page
    useEffect(() => {
        const resetPendingState = () => {
            localStorage.removeItem('synclothe_auth_pending');
            setIsLoggingIn(false);
        };

        window.addEventListener('pageshow', resetPendingState);
        const timeout = setTimeout(resetPendingState, 8000);
        
        return () => {
            window.removeEventListener('pageshow', resetPendingState);
            clearTimeout(timeout);
        };
    }, [isLoggingIn]);

    return (
        <div className="flex flex-col gap-1 w-full">
            <button 
                onClick={handleLogin} 
                disabled={isLoggingIn}
                className={`${className} ${isLoggingIn ? 'opacity-70 cursor-wait pointer-events-none' : ''} flex items-center justify-center gap-2 transition-all duration-300`}
            >
                {isLoggingIn ? (
                    <span className="flex items-center gap-2">
                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span className="animate-pulse">System Redirecting...</span>
                    </span>
                ) : (text || getDefaultText())}
            </button>
            {webViewWarning && (
                <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-[9px] font-mono text-amber-300 leading-tight">
                    ⚠️ {webViewWarning}
                </div>
            )}
        </div>
    );
};

export default LoginButton;
